import React, { useState, useEffect, useRef } from 'react';
import { Button, Drawer, Layout, Tabs, message, Form, Input } from 'antd';
import { MenuOutlined, LockOutlined, LogoutOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import ConversationList from '../components/ConversationList';
import MessageList from '../components/MessageList';
import MessageInput from '../components/MessageInput';
import MediaGenerator from '../components/MediaGenerator';

const { Sider, Content } = Layout;
const { TabPane } = Tabs;

function Chat() {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [agents, setAgents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingBySessionId, setSendingBySessionId] = useState({});
  const [chatUsage, setChatUsage] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeDrawerTab, setActiveDrawerTab] = useState('conversations');
  const [activeTab, setActiveTab] = useState('chat');
  const [refreshing, setRefreshing] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRef = useRef(null);
  const activeSessionRef = useRef(null);
  const messagesRequestRef = useRef(0);

  const [passwordForm] = Form.useForm();
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, sendingBySessionId]);

  useEffect(() => {
    loadAgents();
    loadSessions();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await axios.get('/api/chat/agents', { withCredentials: true });
      setAgents(response.data);
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const loadChatUsage = async (configId) => {
    if (!configId) { setChatUsage(null); return; }
    try {
      const response = await axios.get('/api/ai-media/model-usage', { params: { configId, usageType: 'chat' }, withCredentials: true });
      setChatUsage(response.data);
    } catch {
      setChatUsage(null);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await axios.get('/api/chat/sessions', { withCredentials: true });
      setSessions(response.data);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const refreshChat = async () => {
    await Promise.all([loadAgents(), loadSessions()]);
    if (currentSession?.id) {
      await loadMessages(currentSession.id);
      loadChatUsage(currentSession?.agent?.aiConfig?.id);
    }
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      if (activeTab === 'media') {
        mediaRef.current?.refresh();
      } else {
        await refreshChat();
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'media') {
      mediaRef.current?.refresh();
    } else {
      refreshChat();
    }
  };

  const loadMessages = async (sessionId) => {
    const requestId = ++messagesRequestRef.current;
    setLoading(true);
    try {
      const response = await axios.get(`/api/chat/sessions/${sessionId}/messages`, { withCredentials: true });
      // 防竞态：只有当此请求仍是最新一次，且目标会话仍是当前会话时才写入
      if (requestId !== messagesRequestRef.current) return;
      if (activeSessionRef.current !== sessionId) return;
      setMessages(response.data);
    } catch (err) {
      if (requestId === messagesRequestRef.current) {
        message.error('加载会话消息失败');
      }
    } finally {
      if (requestId === messagesRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSelectSession = async (session) => {
    activeSessionRef.current = session.id;
    setCurrentSession(session);
    setMessages([]);
    setDrawerVisible(false);
    loadChatUsage(session?.agent?.aiConfig?.id);
    await loadMessages(session.id);
  };

  const handleNewConversation = async (agentId) => {
    try {
      const response = await axios.post('/api/chat/sessions', { agentId, title: '新会话' }, { withCredentials: true });
      const newSession = response.data;
      activeSessionRef.current = newSession.id;
      messagesRequestRef.current++; // 使任何在途的历史消息请求失效，避免覆盖新会话
      setSessions([newSession, ...sessions]);
      setCurrentSession(newSession);
      setMessages([]);
      setDrawerVisible(false); // Close the drawer when a new conversation is started
      loadChatUsage(newSession?.agent?.aiConfig?.id);
    } catch (err) {
      message.error('创建会话失败');
    }
  };

  const updateSessionState = (sessionPatch) => {
    if (!sessionPatch?.id) return;

    setSessions(items => items
      .map(item => (item.id === sessionPatch.id ? { ...item, ...sessionPatch } : item))
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)));
    setCurrentSession(item => (item?.id === sessionPatch.id ? { ...item, ...sessionPatch } : item));
  };

  const handleSendMessage = async (content, imageIds) => {
    if (!currentSession) return;
    const sessionId = currentSession.id;

    const pendingUserMessage = {
      id: `local-user-${sessionId}-${Date.now()}`,
      role: 'user',
      content,
      status: 'success',
      pending: true
    };
    const pendingAssistantMessage = {
      id: `local-assistant-${sessionId}-${Date.now()}`,
      role: 'assistant',
      content: '',
      status: 'pending',
      pending: true
    };

    setMessages(items => [...items, pendingUserMessage, pendingAssistantMessage]);
    setSendingBySessionId(items => ({ ...items, [sessionId]: true }));

    try {
      const response = await axios.post(
        `/api/chat/sessions/${sessionId}/messages`,
        { content, imageIds },
        { withCredentials: true }
      );
      const { userMessage, assistantMessage } = response.data;
      updateSessionState(response.data.session);
      setMessages(items => items.map(item => {
        if (item.id === pendingUserMessage.id) return userMessage;
        if (item.id === pendingAssistantMessage.id) return assistantMessage;
        return item;
      }));
    } catch (err) {
      setMessages(items => items.map(item => {
        if (item.id === pendingUserMessage.id) {
          return { ...item, status: 'failed' };
        }
        if (item.id === pendingAssistantMessage.id) {
          return {
            ...item,
            content: err.response?.data?.error || '回复失败，请稍后重试',
            status: 'failed',
            pending: false
          };
        }
        return item;
      }));
      message.error('发送消息失败');
    } finally {
      setSendingBySessionId(items => {
        const nextItems = { ...items };
        delete nextItems[sessionId];
        return nextItems;
      });
    }
    loadChatUsage(currentSession?.agent?.aiConfig?.id);
  };

  const handleDeleteSession = async (session) => {
    try {
      await axios.delete(`/api/chat/sessions/${session.id}`, { withCredentials: true });
      const remaining = sessions.filter(item => item.id !== session.id);
      setSessions(remaining);
      if (currentSession?.id === session.id) {
        activeSessionRef.current = null;
        messagesRequestRef.current++;
        setCurrentSession(null);
        setMessages([]);
      }
      message.success('会话已删除');
    } catch (err) {
      message.error(err.response?.data?.error || '删除会话失败');
    }
  };

  const handleDeleteMessage = async (msg) => {
    if (!currentSession) return;
    if (msg.pending) {
      setMessages(messages.filter(item => item.id !== msg.id));
      return;
    }
    try {
      await axios.delete(
        `/api/chat/sessions/${currentSession.id}/messages/${msg.id}`,
        { withCredentials: true }
      );
      setMessages(messages.filter(item => item.id !== msg.id));
      message.success('消息已删除');
    } catch (err) {
      message.error(err.response?.data?.error || '删除消息失败');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/chat/auth/logout', {}, { withCredentials: true });
      window.location.reload();
    } catch (err) {
      message.error('退出登录失败');
    }
  };

  const handleChangePassword = async (values) => {
    setPasswordLoading(true);
    try {
      const response = await axios.post(
        '/api/chat/auth/change-password',
        {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword
        },
        { withCredentials: true }
      );
      if (response.data.success) {
        message.success('密码修改成功');
        passwordForm.resetFields();
        setDrawerVisible(false);
      }
    } catch (err) {
      message.error(err.response?.data?.error || '修改密码失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  const currentSessionId = currentSession?.id;
  const currentSessionSending = currentSessionId ? Boolean(sendingBySessionId[currentSessionId]) : false;
  const agentSupportsImage = currentSession?.agent?.aiConfig?.supportsImageInput;

  const conversationList = (
    <ConversationList
      sessions={sessions}
      currentSession={currentSession}
      onSelect={handleSelectSession}
      agents={agents}
      onNewConversation={handleNewConversation}
      onDelete={handleDeleteSession}
    />
  );

  const renderDrawerContent = () => {
    switch (activeDrawerTab) {
      case 'conversations':
        return conversationList;
      case 'password':
        return (
          <div className="drawer-tab-content">
            <div className="drawer-tab-header">
              <LockOutlined />
              <span>修改密码</span>
            </div>
            <div className="drawer-tab-body">
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleChangePassword}
                autoComplete="off"
              >
                <Form.Item
                  label="旧密码"
                  name="oldPassword"
                  rules={[{ required: true, message: '请输入旧密码' }]}
                >
                  <Input.Password placeholder="请输入旧密码" />
                </Form.Item>
                <Form.Item
                  label="新密码"
                  name="newPassword"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 8, message: '密码至少8位' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        if (!/[a-zA-Z]/.test(value) || !/\d/.test(value)) {
                          return Promise.reject(new Error('密码必须同时包含字母和数字'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Input.Password placeholder="至少8位，包含字母和数字" />
                </Form.Item>
                <Form.Item
                  label="确认新密码"
                  name="confirmPassword"
                  rules={[
                    { required: true, message: '请确认新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致'));
                      }
                    })
                  ]}
                >
                  <Input.Password placeholder="请再次输入新密码" />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={passwordLoading}
                    block
                  >
                    确认修改
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        );
      case 'logout':
        return (
          <div className="drawer-tab-content">
            <div className="drawer-tab-header">
              <LogoutOutlined />
              <span>退出登录</span>
            </div>
            <div className="drawer-tab-body">
              <p className="drawer-placeholder">确定要退出登录吗？</p>
              <Button type="primary" danger onClick={handleLogout} block>
                确认退出
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const chatPanel = (
    <Layout className="chat-panel">
      <Content className="chat-content">
        <div className="chat-header">
          <div className="chat-header-left">
            {currentSession && (
              <span className="chat-session-title">
                {currentSession.title || '新会话'}
              </span>
            )}
          </div>
          <div className="chat-header-right">
            {chatUsage && chatUsage.limit > 0 && (
              <span style={{ marginRight: 12, fontSize: 13, color: chatUsage.remaining <= 0 ? '#ff4d4f' : chatUsage.remaining <= 2 ? '#faad14' : '#999' }}>
                对话今日剩 {chatUsage.remaining}/{chatUsage.limit} 次
              </span>
            )}
            <Button
              icon={<MenuOutlined />}
              onClick={() => {
                setActiveDrawerTab('conversations');
                setDrawerVisible(true);
              }}
              className="chat-menu-button"
            />
          </div>
        </div>
        <div className="chat-messages">
          <MessageList messages={messages} loading={loading} onDelete={handleDeleteMessage} assistantName={currentSession?.agent?.displayName} />
          <div ref={messagesEndRef} />
        </div>
        <div className="chat-input">
          <MessageInput
            key={currentSessionId || 'no-session'}
            onSend={handleSendMessage}
            disabled={!currentSession || currentSessionSending}
            supportsImage={agentSupportsImage}
            sessionId={currentSessionId}
          />
        </div>
      </Content>
      <Drawer
        title={null}
        placement="right"
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={320}
        className="chat-right-drawer"
        bodyStyle={{ padding: 0 }}
      >
        <div className="drawer-tabs">
          <div
            className={`drawer-tab-item ${activeDrawerTab === 'conversations' ? 'active' : ''}`}
            onClick={() => setActiveDrawerTab('conversations')}
          >
            <MenuOutlined />
            <span>会话</span>
          </div>
          <div
            className={`drawer-tab-item ${activeDrawerTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveDrawerTab('password')}
          >
            <LockOutlined />
            <span>修改密码</span>
          </div>
          <div
            className={`drawer-tab-item ${activeDrawerTab === 'logout' ? 'active' : ''}`}
            onClick={() => setActiveDrawerTab('logout')}
          >
            <LogoutOutlined />
            <span>退出登录</span>
          </div>
        </div>
        <div className="drawer-content">
          {renderDrawerContent()}
        </div>
      </Drawer>
    </Layout>
  );

  return (
    <Layout className="chat-app">
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        className="chat-tabs"
        tabBarExtraContent={
          <Button
            type="text"
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            className="chat-refresh-button"
            title="刷新"
          />
        }
      >
        <TabPane tab="AI 对话" key="chat">
          {chatPanel}
        </TabPane>
        <TabPane tab="AI 创作" key="media">
          <div className="media-tab">
            <MediaGenerator ref={mediaRef} />
          </div>
        </TabPane>
      </Tabs>
    </Layout>
  );
}

export default Chat;
